import { partesDelTexto, type ParteDelTexto } from '@athena/shared';
import type { Idioma } from '../../../i18n/index.js';
import type { RespuestaDeAthena } from '../../../common/services/tipos.js';
import { TextoConMarcas } from './TextoConMarcas.js';

/**
 * El texto de un `Parrafo` con su Markdown acotado (Iván, 2026-10-02): subtítulos, listas y citas
 * destacadas, para que una respuesta larga se lea por partes y no como un muro.
 *
 * **Sin HTML libre.** `partesDelTexto` (en `@athena/shared`, el mismo que usan el validador y el
 * juez) decide la estructura y React escapa el texto: nada de lo que escriba el modelo se convierte
 * en marcado. Dentro de cada parte, la negrita, la cursiva y las marcas [n] las pinta
 * `TextoConMarcas`, igual que en un párrafo de siempre.
 *
 * **Jerarquía.** La pregunta es el h1 de la página (o h2 en un seguimiento); aquí `##` es h3 y `###`
 * h4, siempre por debajo. Un texto sin Markdown de bloque (todas las respuestas guardadas hasta hoy)
 * sale como un solo `<p>`, exactamente como antes.
 */
export function TextoEnriquecido({
  texto,
  respuesta,
  idioma,
}: {
  readonly texto: string;
  readonly respuesta: RespuestaDeAthena;
  readonly idioma: Idioma;
}) {
  const partes = partesDelTexto(texto);
  return (
    <>
      {partes.map((parte, i) => (
        <Parte key={`${String(i)}-${parte.tipo}`} parte={parte} primera={i === 0} respuesta={respuesta} idioma={idioma} />
      ))}
    </>
  );
}

const PROSA = 'font-(family-name:--text) text-[17px] leading-[1.62] text-(--ink)';

function Parte({
  parte,
  primera,
  respuesta,
  idioma,
}: {
  readonly parte: ParteDelTexto;
  readonly primera: boolean;
  readonly respuesta: RespuestaDeAthena;
  readonly idioma: Idioma;
}) {
  switch (parte.tipo) {
    case 'titulo':
      // Los títulos no llevan cifras ni marcas (lo exige el validador): solo negrita o cursiva, si acaso.
      return parte.nivel === 2 ? (
        <h3
          className={`mb-3 font-(family-name:--serif) text-[22px] leading-[1.25] font-bold text-balance text-(--ink) min-[700px]:text-[24px] ${primera ? 'mt-0' : 'mt-9'}`}
        >
          <TextoConMarcas texto={parte.texto} respuesta={respuesta} idioma={idioma} />
        </h3>
      ) : (
        <h4
          className={`mb-2 flex items-baseline gap-2.5 font-(family-name:--text) text-[17.5px] leading-[1.35] font-semibold text-(--ink) ${primera ? 'mt-0' : 'mt-7'}`}
        >
          {/* Una raya corta de color para que el subtítulo se vea al pasar la vista, sin gritar. */}
          <span aria-hidden='true' className='inline-block h-[3px] w-[14px] shrink-0 translate-y-[-4px] rounded-full bg-(--teal-deep)' />
          <span>
            <TextoConMarcas texto={parte.texto} respuesta={respuesta} idioma={idioma} />
          </span>
        </h4>
      );

    case 'parrafo':
      return (
        <p className={`mb-[18px] ${PROSA}`}>
          <TextoConMarcas texto={parte.texto} respuesta={respuesta} idioma={idioma} />
        </p>
      );

    case 'lista': {
      const Lista = parte.ordenada ? 'ol' : 'ul';
      return (
        <Lista
          className={`mb-[18px] flex flex-col gap-2.5 pl-6 ${PROSA} ${
            parte.ordenada
              ? 'list-decimal marker:font-(family-name:--mono) marker:text-[14px] marker:text-(--teal-deep)'
              : 'list-disc marker:text-(--teal-deep)'
          }`}
        >
          {parte.items.map((item, i) => (
            <li key={`${String(i)}-${item.slice(0, 24)}`} className='pl-1'>
              <TextoConMarcas texto={item} respuesta={respuesta} idioma={idioma} />
            </li>
          ))}
        </Lista>
      );
    }

    case 'cita':
      return <CitaDestacada texto={parte.texto} marca={parte.marca} respuesta={respuesta} idioma={idioma} />;
  }
}

/**
 * Lo que dice un estudio, destacado, con su fuente y su página debajo: la frase y de quién es,
 * juntas, que es lo que hace que una cita se pueda comprobar. Sin marca (el validador no lo deja
 * pasar, pero una respuesta guardada podría traerlo), se pinta como párrafo: una cita sin fuente
 * no merece un marco que la haga parecer más sólida.
 */
function CitaDestacada({
  texto,
  marca,
  respuesta,
  idioma,
}: {
  readonly texto: string;
  readonly marca: number | null;
  readonly respuesta: RespuestaDeAthena;
  readonly idioma: Idioma;
}) {
  const referencia = marca === null ? undefined : respuesta.referencias.find(r => r.n === marca);
  const fuente = referencia === undefined ? undefined : respuesta.fuentes.find(f => f.hechoId === referencia.hechoId);
  if (marca === null || fuente === undefined) {
    return (
      <p className={`mb-[18px] ${PROSA}`}>
        <TextoConMarcas texto={marca === null ? texto : `${texto} [${String(marca)}]`} respuesta={respuesta} idioma={idioma} />
      </p>
    );
  }

  const pagina = fuente.pagina === null ? null : `p. ${String(fuente.pagina)}`;
  return (
    <figure className='mx-0 mt-2 mb-6 rounded-r-lg border-l-[3px] border-(--teal-deep) bg-(--chat-bg) py-4 pr-5 pl-5'>
      <blockquote className='m-0 font-(family-name:--serif) text-[19px] leading-[1.5] text-(--ink) italic min-[700px]:text-[20px]'>
        <TextoConMarcas texto={texto} respuesta={respuesta} idioma={idioma} />
      </blockquote>
      <figcaption className='mt-3 flex flex-wrap items-baseline gap-x-2 font-(family-name:--text) text-[13.5px] text-(--ink-soft)'>
        <span className='font-semibold text-(--ink)'>{fuente.editor}</span>
        {fuente.anio === null ? null : <span>{String(fuente.anio)}</span>}
        {pagina === null ? null : <span className='font-(family-name:--mono) text-[12.5px] text-(--ink-faint)'>{pagina}</span>}
        <a href={`#f${String(marca)}-${respuesta.preguntaId}`} className='font-(family-name:--mono) text-[12px] text-(--teal-deep) no-underline'>
          [{String(marca)}]
        </a>
      </figcaption>
    </figure>
  );
}
